/**
 * MobileActionBar.js - Floating action bar for mobile view
 * Shows contextual actions based on current tab/view
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, X, Briefcase, UserPlus, Check, Eye, Edit, 
  ChevronUp, Car, FileText
} from 'lucide-react';
import { Button } from '../ui/button';

const MobileActionBar = ({ 
  actions = [], // Array of { id, label, icon, onClick, variant }
  primaryAction = null, // Main FAB action
  position = 'bottom-right' // 'bottom-right', 'bottom-center'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (actions.length === 0 && !primaryAction) return null;
  
  const iconMap = {
    Plus, Briefcase, UserPlus, Check, Eye, Edit, Car, FileText
  };
  
  const positionClasses = {
    'bottom-right': 'right-4 bottom-20',
    'bottom-center': 'left-1/2 -translate-x-1/2 bottom-20'
  };
  
  // If only one action, show it directly
  if (actions.length === 1 && !primaryAction) {
    const action = actions[0];
    const Icon = iconMap[action.icon] || Plus;
    return (
      <div className={`fixed ${positionClasses[position]} z-40 md:hidden`}>
        <Button
          onClick={action.onClick}
          className={`w-14 h-14 rounded-full shadow-lg ${
            action.variant === 'success' ? 'bg-green-600 hover:bg-green-700' :
            action.variant === 'warning' ? 'bg-amber-600 hover:bg-amber-700' :
            'bg-[var(--primary)] hover:bg-[var(--primary-hover)]'
          }`}
          data-testid={`mobile-action-${action.id}`}
        >
          <Icon className="w-6 h-6 text-white" />
        </Button>
      </div>
    );
  }
  
  // Multiple actions - expandable FAB
  return (
    <div className={`fixed ${positionClasses[position]} z-40 md:hidden`}>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-16 right-0 space-y-2 mb-2"
          >
            {actions.map((action, index) => {
              const Icon = iconMap[action.icon] || Plus;
              return (
                <motion.div
                  key={action.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-end gap-2"
                >
                  <span className="bg-black/80 text-white text-sm px-3 py-1 rounded-full whitespace-nowrap">
                    {action.label}
                  </span>
                  <Button
                    onClick={() => {
                      action.onClick();
                      setIsExpanded(false);
                    }}
                    className={`w-12 h-12 rounded-full shadow-lg ${
                      action.variant === 'success' ? 'bg-green-600 hover:bg-green-700' :
                      action.variant === 'warning' ? 'bg-amber-600 hover:bg-amber-700' :
                      action.variant === 'danger' ? 'bg-red-600 hover:bg-red-700' :
                      'bg-[var(--primary)] hover:bg-[var(--primary-hover)]'
                    }`}
                    data-testid={`mobile-action-${action.id}`}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </Button>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Main FAB toggle */}
      <Button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-14 h-14 rounded-full shadow-lg transition-transform ${
          isExpanded ? 'rotate-45 bg-gray-700' : 'bg-[var(--primary)]'
        }`}
        data-testid="mobile-fab-toggle"
      >
        {isExpanded ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <ChevronUp className="w-6 h-6 text-white" />
        )}
      </Button>
    </div>
  );
};

export default MobileActionBar;
